import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchBox, ListToolbar } from '../../components/ListFilters.tsx';
import {
  Board,
  CardCover,
  CardGrid,
  ConversationLayout,
  FactList,
  ListDetail,
  RecordLayout,
  StateView,
  useScreenActions,
} from '../../components/patterns/index.ts';
import { Badge, Button, Field, Input, Table } from '../../components/ui.tsx';

import { CUSTOMERS, useMoney } from './pattern-samples.ts';

export function ListExample() {
  const { t } = useTranslation();
  const s = (k: string) => t(`patternsPage.sample.${k}`);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const shown = CUSTOMERS.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));
  const chosen = CUSTOMERS.find((c) => c.id === open);
  const actions = useScreenActions(
    <Button>
      <Plus size={16} aria-hidden />
      {s('newCustomer')}
    </Button>,
  );
  const list = (
    <div className="flex flex-col gap-3">
      <ListToolbar action={actions}>
        <SearchBox value={q} onChange={setQ} placeholder={s('search')} />
      </ListToolbar>
      {shown.length === 0 ? (
        <div className="ui-panel rounded-lg bg-sheet">
          <StateView
            kind="empty"
            action={<Button onClick={() => setQ('')}>{s('newCustomer')}</Button>}
          />
        </div>
      ) : (
        <Table head={[s('name'), s('city'), s('status')]}>
          {shown.map((c) => (
            <tr key={c.id} onClick={() => setOpen(c.id)} className="cursor-pointer">
              <td>
                <button
                  type="button"
                  className="font-medium hover:underline"
                  onClick={() => setOpen(c.id)}
                >
                  {c.name}
                </button>
              </td>
              <td className="text-muted">{c.city}</td>
              <td>
                <Badge tone={c.late ? 'off' : 'ok'}>{s(c.late ? 'late' : 'active')}</Badge>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
  return (
    <ListDetail
      list={list}
      detail={chosen ? <CustomerRecord customer={chosen} /> : null}
      detailTitle={chosen?.name}
      onClose={() => setOpen(null)}
      placeholder={<StateView kind="empty" compact title={s('pickCustomer')} />}
    />
  );
}

export function CustomerRecord({ customer }: { customer: (typeof CUSTOMERS)[number] }) {
  const { t, i18n } = useTranslation();
  const money = useMoney();
  const s = (k: string) => t(`patternsPage.sample.${k}`);
  return (
    <RecordLayout
      title={customer.name}
      meta={
        <>
          <code className="font-mono text-xs text-muted">
            CLI-{customer.id.slice(1).padStart(6, '0')}
          </code>
          <Badge tone={customer.late ? 'off' : 'ok'}>{s(customer.late ? 'late' : 'active')}</Badge>
        </>
      }
      aside={
        <FactList
          items={[
            { label: s('owner'), value: 'Marina Souza' },
            {
              label: s('since'),
              value: new Date(Date.UTC(2024, 2, 12)).toLocaleDateString(i18n.language, {
                timeZone: 'UTC',
              }),
            },
            {
              label: s('balance'),
              value: <span className="tabular-nums">{money(customer.balance)}</span>,
            },
          ]}
        />
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={s('name')}>
          <Input defaultValue={customer.name} />
        </Field>
        <Field label={s('city')}>
          <Input defaultValue={customer.city} />
        </Field>
      </div>
    </RecordLayout>
  );
}

export function BoardExample() {
  const { t } = useTranslation();
  const money = useMoney();
  const s = (k: string) => t(`patternsPage.sample.${k}`);
  const deals = CUSTOMERS.map((c, i) => ({
    ...c,
    stage: ['lead', 'proposal', 'proposal', 'won'][i]!,
  }));
  return (
    <Board
      label={s('customers')}
      columns={(['lead', 'proposal', 'won'] as const).map((id) => ({
        id,
        title: s(`stages.${id}`),
      }))}
      items={deals}
      columnOf={(d) => d.stage}
      keyOf={(d) => d.id}
      renderCard={(d) => (
        <div className="flex flex-col gap-1">
          <span className="font-medium">{d.name}</span>
          <span className="text-xs text-muted">{d.city}</span>
          <span className="tabular-nums">{money(d.balance)}</span>
        </div>
      )}
    />
  );
}

export function GalleryExample() {
  const money = useMoney();
  return (
    <CardGrid
      items={CUSTOMERS}
      keyOf={(c) => c.id}
      renderCard={(c) => (
        <>
          <CardCover name={c.name} />
          <div className="flex flex-col gap-0.5 p-3 text-sm">
            <span className="font-medium">{c.name}</span>
            <span className="text-xs text-muted">{c.city}</span>
            <span className="pt-1 tabular-nums">{money(c.balance)}</span>
          </div>
        </>
      )}
    />
  );
}

export function ConversationExample() {
  const { t } = useTranslation();
  const s = (k: string, o?: Record<string, string>) => t(`patternsPage.sample.${k}`, o);
  const [open, setOpen] = useState<string | null>('c2');
  const [extra, setExtra] = useState<string[]>([]);
  const who = CUSTOMERS.find((c) => c.id === open);
  return (
    <ConversationLayout
      threads={CUSTOMERS.map((c, i) => ({
        id: c.id,
        title: c.name,
        preview: s('msg1'),
        unread: i === 0 ? 2 : 0,
      }))}
      openId={open}
      onOpen={setOpen}
      header={
        who && (
          <span className="text-sm font-medium">{s('conversationWith', { name: who.name })}</span>
        )
      }
      messages={[
        { id: 'm1', author: who?.name ?? '', when: '09:12', text: s('msg1') },
        { id: 'm2', author: 'Marina Souza', when: '09:20', text: s('msg2'), mine: true },
        { id: 'm3', author: who?.name ?? '', when: '09:21', text: s('msg3') },
        ...extra.map((text, i) => ({
          id: `x${i}`,
          author: 'Marina Souza',
          when: '09:30',
          text,
          mine: true,
        })),
      ]}
      onSend={(text) => setExtra((x) => [...x, text])}
    />
  );
}
